import React from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { Flower2, LogOut, User, ShieldCheck, Menu, X, Sparkles, Camera, Trophy } from "lucide-react";
import PointsBadge from "./PointsBadge";
import NotificationBell from "./NotificationBell";

export default function Navbar() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = React.useState(false);

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  const linkCls = ({ isActive }) =>
    `px-3 py-2 rounded-full text-sm font-medium transition-colors ${
      isActive ? "text-[#D96C4A]" : "text-[#2D3B31] hover:text-[#D96C4A]"
    }`;

  return (
    <nav
      className="sticky top-0 z-50 backdrop-blur-xl border-b"
      style={{ backgroundColor: "rgba(250,248,245,0.85)", borderColor: "#EAE5D9" }}
      data-testid="main-navbar"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-12 flex items-center justify-between h-16">
        <Link to="/" className="flex items-center gap-2" data-testid="nav-logo-link">
          <Flower2 size={28} color="#D96C4A" />
          <span className="font-serif-display text-2xl font-medium text-[#2D3B31]">
            Gītā Sabhā
          </span>
        </Link>

        <div className="hidden md:flex items-center gap-1">
          <NavLink to="/" end className={linkCls} data-testid="nav-home">Home</NavLink>
          <NavLink to="/chapters" className={linkCls} data-testid="nav-chapters">Chapters</NavLink>
          <NavLink to="/guidance" className={linkCls} data-testid="nav-guidance">
            <span className="inline-flex items-center gap-1">
              <Sparkles size={14} /> Ask Krishna
            </span>
          </NavLink>
          <NavLink to="/reflect" className={linkCls} data-testid="nav-reflect">
            <span className="inline-flex items-center gap-1">
              <Camera size={14} /> Reflect
            </span>
          </NavLink>
          <NavLink to="/leaderboard" className={linkCls} data-testid="nav-leaderboard">
            <span className="inline-flex items-center gap-1">
              <Trophy size={14} /> Honors
            </span>
          </NavLink>
          {user && user !== false && (
            <NavLink to="/profile" className={linkCls} data-testid="nav-profile">Profile</NavLink>
          )}
          {user && user !== false && user.role === "admin" && (
            <NavLink to="/admin" className={linkCls} data-testid="nav-admin">
              <span className="inline-flex items-center gap-1">
                <ShieldCheck size={16} /> Admin
              </span>
            </NavLink>
          )}
        </div>

        <div className="hidden md:flex items-center gap-3">
          <NotificationBell />
          <PointsBadge />
          {user === null ? (
            <div className="w-24 h-9 rounded-full bg-[#F3EFE9] animate-pulse" />
          ) : user === false ? (
            <>
              <Link to="/login" className="btn-outline" data-testid="nav-login-btn">
                Sign in
              </Link>
              <Link to="/register" className="btn-accent" data-testid="nav-register-btn">
                Begin journey
              </Link>
            </>
          ) : (
            <>
              <span className="text-sm text-[#5C6B5F] inline-flex items-center gap-2" data-testid="nav-user-name">
                <User size={18} color="#5C6B5F" />
                {user.name}
              </span>
              <button onClick={handleLogout} className="btn-outline" data-testid="nav-logout-btn">
                <span className="inline-flex items-center gap-2">
                  <LogOut size={16} /> Logout
                </span>
              </button>
            </>
          )}
        </div>

        <button
          className="md:hidden p-2"
          onClick={() => setOpen(!open)}
          data-testid="nav-mobile-toggle"
          aria-label="Toggle menu"
        >
          {open ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      {open && (
        <div className="md:hidden border-t" style={{ borderColor: "#EAE5D9" }}>
          <div className="px-6 py-4 flex flex-col gap-2">
            <NavLink to="/" end className={linkCls} onClick={() => setOpen(false)} data-testid="mnav-home">Home</NavLink>
            <NavLink to="/chapters" className={linkCls} onClick={() => setOpen(false)} data-testid="mnav-chapters">Chapters</NavLink>
            <NavLink to="/guidance" className={linkCls} onClick={() => setOpen(false)} data-testid="mnav-guidance">Ask Krishna</NavLink>
            <NavLink to="/reflect" className={linkCls} onClick={() => setOpen(false)} data-testid="mnav-reflect">Reflect</NavLink>
            <NavLink to="/leaderboard" className={linkCls} onClick={() => setOpen(false)} data-testid="mnav-leaderboard">Honors</NavLink>
            {user && user !== false && (
              <NavLink to="/profile" className={linkCls} onClick={() => setOpen(false)} data-testid="mnav-profile">Profile</NavLink>
            )}
            {user && user !== false && user.role === "admin" && (
              <NavLink to="/admin" className={linkCls} onClick={() => setOpen(false)} data-testid="mnav-admin">Admin</NavLink>
            )}
            <div className="pt-2 flex gap-2">
              {user === false ? (
                <>
                  <Link to="/login" className="btn-outline flex-1 text-center" onClick={() => setOpen(false)} data-testid="mnav-login">Sign in</Link>
                  <Link to="/register" className="btn-accent flex-1 text-center" onClick={() => setOpen(false)} data-testid="mnav-register">Begin</Link>
                </>
              ) : user && user !== false ? (
                <button onClick={() => { setOpen(false); handleLogout(); }} className="btn-outline flex-1" data-testid="mnav-logout">
                  Logout
                </button>
              ) : null}
            </div>
          </div>
        </div>
      )}
    </nav>
  );
}
